/*
 * Copyright © 2021 Lexcelon LLC <info@lexcelon.com>
 * Licensed for non-distributable use
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Button,
  CircularProgress,
  Drawer,
  Toolbar,
  IconButton,
  Menu,
  Hidden,
  MenuItem,
  Typography
} from '@mui/material';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Header specific imports
import SideMenu from './SideMenu';
import { isSelected } from './util';

// Custom App Bar
function Header({
  buttonType = 'hamburger',
  logo,
  logoDescription,
  links,
  backgroundColor,
  textColor,
  accentColor = null,
  drawer = false,
  onLogout = null,
  title = '',
  isLoading = false,
  onOpen = () => undefined,
  hideMenu = false,
  pathname,
}) {
  const [linkMenuAnchorEl, setLinkMenuAnchorEl] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [isSidePanelOpen, setSidePanelOpen] = useState(false);

  const handleLinkMenuClick = (event, link) => {
    setLinkMenuAnchorEl(event.currentTarget);
    setSelectedLink(link);
  };

  const handleLinkMenuClose = () => {
    setLinkMenuAnchorEl(null);
    setSelectedLink(null);
  };

  const toggleSideMenu = () => {
    if (!isSidePanelOpen) onOpen();
    setSidePanelOpen(!isSidePanelOpen);
  };

  const closeSideMenu = () => {
    setSidePanelOpen(false);
  };

  const logoutUser = () => {
    closeSideMenu();
    if (onLogout) onLogout();
  };

  return (
    <AppBar style={{ backgroundColor }} position="static">
      <Toolbar>
        {/* LOGO */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <Link to="/">
            <img
              width={150}
              height={50}
              src={logo}
              alt={logoDescription}
              title={logoDescription}
              style={{ objectFit: 'contain', cursor: 'pointer' }}
            />
          </Link>
          {title && (
            <Typography variant='h1' color={textColor} style={{ marginLeft: 15, fontSize: 23, textTransform: 'uppercase' }}>{title}</Typography>
          )}
        </div>

        {!hideMenu && (
          isLoading ? (
            <CircularProgress size={20} style={{ color: 'white' }} />
          ) : (
            <>
              {/* MOBILE VIEW */}
              <Hidden mdUp implementation="css">
                <div style={{ flex: 1, textAlign: 'right' }}>
                  {/* HAMBURGER BUTTON */}
                  {buttonType === 'hamburger' && (
                    <IconButton
                      aria-controls="nav-menu"
                      aria-haspopup="true"
                      onClick={toggleSideMenu}
                    >
                      <MenuIcon style={{ color: textColor }} />
                    </IconButton>
                  )}
                  {/* MENU BUTTON */}
                  {buttonType === 'menu' && (
                    <Button
                      aria-controls="nav-menu"
                      aria-haspopup="true"
                      onClick={toggleSideMenu}
                      variant="text"
                      style={{
                        color: textColor,
                        fontSize: '14px',
                        textTransform: 'none',
                      }}
                    >
                      MENU
                      <MenuIcon style={{ color: textColor, fontSize: '20px', marginLeft: '2px' }} />
                    </Button>
                  )}
                </div>
              </Hidden>
              {/* DRAWER MENU */}
              <Drawer
                anchor={'right'}
                open={isSidePanelOpen}
                onClose={toggleSideMenu}
              >
                <SideMenu
                  toggle={toggleSideMenu}
                  links={links}
                  logo={logo}
                  logoDescription={logoDescription}
                  backgroundColor={backgroundColor}
                  textColor={textColor}
                  accentColor={accentColor}
                  logoutUser={onLogout == null ? null : logoutUser}
                  pathname={pathname}
                />
              </Drawer>
              {/* FULL SCREEN VIEW */}
              <Hidden mdDown implementation="css">
                {drawer ? (
                  <div style={{ flex: 1, textAlign: 'right' }}>
                    {/* HAMBURGER BUTTON */}
                    {buttonType === 'hamburger' && (
                      <IconButton
                        aria-controls="nav-menu"
                        aria-haspopup="true"
                        onClick={toggleSideMenu}
                      >
                        <MenuIcon style={{ color: textColor }} />
                      </IconButton>
                    )}
                    {/* MENU BUTTON */}
                    {buttonType === 'menu' && (
                      <Button
                        aria-controls="nav-menu"
                        aria-haspopup="true"
                        onClick={toggleSideMenu}
                        variant="text"
                        style={{
                          color: textColor,
                          fontSize: '14px',
                          textTransform: 'none',
                        }}
                      >
                        MENU
                        <MenuIcon style={{ color: textColor, fontSize: '20px', marginLeft: '2px' }} />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      flex: 3,
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {links.map((link, index) => (
                      <div key={index}>
                        <Button
                          variant="text"
                          component={Link}
                          style={{
                            marginLeft: '10px',
                            textAlign: 'center',
                            fontSize: '14px',
                            color:
                              !accentColor ||
                              isSelected(pathname, link.slug)
                                ? textColor
                                : accentColor,
                            textDecoration:
                              !accentColor &&
                              isSelected(pathname, link.slug)
                                ? 'underline'
                                : undefined,
                            textUnderlineOffset: '3px',
                          }}
                          to={link.subLinks ? '#' : link.slug}
                          aria-haspopup={link.subLinks ? 'true' : 'false'}
                          onClick={
                            link.subLinks
                              ? e => handleLinkMenuClick(e, link)
                              : undefined
                          }
                        >
                          {link.text}
                          {link.subLinks &&
                            (linkMenuAnchorEl &&
                            selectedLink?.slug === link.slug ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            ))}
                        </Button>
                        {selectedLink?.subLinks && (
                          <Menu
                            id="simple-menu"
                            anchorEl={linkMenuAnchorEl}
                            keepMounted
                            open={Boolean(linkMenuAnchorEl)}
                            onClose={handleLinkMenuClose}
                            elevation={0}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'center',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'center',
                            }}
                          >
                            {selectedLink?.subLinks?.map(
                              (subLink, subLinkIndex) => (
                                <MenuItem
                                  key={subLinkIndex}
                                  onClick={handleLinkMenuClose}
                                  component={Link}
                                  to={selectedLink?.slug + subLink.slug}
                                  style={{ fontSize: '14px' }}
                                >
                                  {subLink.text}
                                </MenuItem>
                              )
                            )}
                          </Menu>
                        )}
                      </div>
                    ))}
                    {onLogout != null && (
                      <Button
                        variant="text"
                        style={{
                          marginLeft: '10px',
                          textAlign: 'center',
                          fontSize: '14px',
                          color: textColor,
                        }}
                        onClick={logoutUser}
                      >
                        Logout
                      </Button>
                    )}
                  </div>
                )}
              </Hidden>
            </>
          )
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Header;